import * as React from "react";

import Info from "@material-ui/icons/InfoRounded";
import { is_empty, is_text_without_space } from "../../../utils/validations";
import styles from "./coupon.module.css";

import {
  Avatar,
  Checkbox,
  Chip,
  creatorToolTheme,
  ThemeProvider,
  Tooltip,
  Radio,
} from "@my-scoot/component-library-legacy";
import useStyles from "./discounts.styles";

import {
  ExlyCustomSelect,
  ExlyDatePicker,
  ExlyDropdown,
  ExlyInput,
  ExlyOfferingSelect,
} from "common/form";
import ExlyAutoComplete from "common/form/ExlyAutoComplete";
import ExlyCheckbox from "common/form/ExlyCheckbox";
import { Field, Form } from "react-final-form";
import {
  getUserCurrency,
  isInternationalTimezone,
} from "../../../utils/AuthUtil";
import useCreateDiscounts from "./useCreateDiscounts";
import { CircularProgress } from "@material-ui/core";
import ExlyRadioGroup from "common/form/ExlyRadioGroup/Index";
import { checkIsAlphaNumeric } from "features/Common/modules/Form/utils/Form.validations.utils";
import { checkIsArrayEmpty } from "@my-scoot/exly-react-component-lib";
import CustomCodeSelect from "ui/pages/CustomCodes/components/HelperComponents/CustomCodeSelect";
import { ComboPageOption } from "ui/pages/CustomCodes/components/HelperComponents/CombosPageOption";
import EmptySalesPageView from "ui/pages/CustomCodes/components/HelperComponents/EmptySalesPageView";
import {
  discount_form_keys,
  discount_application_scope_options,
  instalment_discount_types_options,
  instalment_discount_types,
  DISCOUNT_PAYMENT_PLAN_TYPE,
} from "features/Discount/constants/Discount.constants";
import { PAYMENT_PLAN_TYPE } from "constants/schedule";
import { format } from "date-fns";
import { DEFAULT_YEAR_MONTH_DATE_FORMAT } from "constants/date-fns/dateTime.constant";
import { LTO_SUPPORTED_CURRENCIES } from "ui/modules/LimitedOfferModalContent/constants";
import { useFetchCustomCodeComboPage } from "ui/pages/CustomCodes/hooks/useFetchCustomCodeComboPage";
import { fetchOfferingVariations } from "features/OfferingVariations/utils/OfferingVariations.utils";
import { logError } from "utils/error";
import { isRequestSuccessful } from "utils/Utils";
import {
  COUPON_APPLICABLE_OPTIONS,
  COUPON_APPLICABLE_TYPE,
  DISCOUNT_CURRENCY_OPTIONS,
  DISCOUNT_TYPE,
  DISCOUNT_TYPE_OPTIONS,
} from "./discounts.constants";
import { LINK_TYPES } from "features/PaymentLinks/modules/CreatePaymentLinkModal/constants/CreatePaymentLinkModal.constants";
import OfferingSearchHeader from "./components/OfferingSearchHeader";

const CreateDiscounts = React.forwardRef(
  (
    { onSubmit, component: WrapperComponent, selectedRecordData, ...restProps },
    ref
  ) => {
    const classes = useStyles();
    const {
      onChangeAudience,
      form_fields,
      CustomerEligibility,
      offeringsOptions,
      infiniteOptions,
      offeringsLoading,
      offeringsLastElementRef,
      searchQuery,
      setSearchQuery,
      debouncedSearchQuery,
      audienceOptions,
      audienceLastElementRef,
      audienceSearchQuery,
      setAudienceSearchQuery,
      resetAudienceFilter,
      onCustomerEligibilityChange,
      addDiscountCoupon,
      editDiscountCoupon,
      handleDeleteOption,
      loading,
      customerEligibilityRadioOptions,
      formatAudienceKey,
      recipientsLoading,
      comboPagesOptions,
      comboPagesLoader,
      lastElementComboPagesRef,
    } = useCreateDiscounts({ ...restProps, selectedRecordData });

    const isEdit = !is_empty(selectedRecordData);

    const formRef = React.useRef(null);

    const { listingMultiSelect } = restProps;

    React.useImperativeHandle(ref, () => ({
      triggerSave() {
        if (!formRef.current) return;

        const { submit } = formRef.current;

        submit();
      },
      form: formRef.current,
    }));

    const validateDiscountCode = (discountCode) => {
      if (is_empty(discountCode)) return "Discount code can't be empty!";

      if (is_text_without_space(discountCode))
        return "You can't use blank spaces in the discount code.";

      if (!checkIsAlphaNumeric(discountCode))
        return "Only Alphanumeric characters are allowed";

      if (discountCode.length > 10)
        return "Discount code length can't be more than 10 characters";

      return null;
    };

    const validateDiscountValue = ({ discountValue, type }) => {
      if (is_empty(discountValue)) return "Discount value can't be empty!";

      if (
        discountValue < 1 ||
        (discountValue > 100 && type == form_fields.percentage_type)
      )
        return "Discount Percentage should be between 1-100";

      if (isNaN(discountValue)) return "Value cannot be non numeric";

      return null;
    };

    const validateUsageLimit = ({ usageLimit }) => {
      if (is_empty(usageLimit)) return "Usage limit can't be empty!";
      if (parseInt(usageLimit) < 1)
        return "Usage limit should be greater than 0.";
      return null;
    };

    const validateSelectOfferings = (offerings) =>
      is_empty(offerings) ? "Select offerings" : null;

    const validateSelectVariations = (variations) =>
      is_empty(variations) ? "Select variations" : null;

    const LoadingState = () => (
      <div className={classes.loading}>
        <CircularProgress size={24} color="primary" />
        <div>Getting things ready...</div>
      </div>
    );

    if ((loading || recipientsLoading) && !WrapperComponent)
      return <LoadingState />;

    const initialValues = React.useMemo(() => {
      return isEdit
        ? {
            ...selectedRecordData,
            ...selectedRecordData.metadata,
            loading: false,
            combo_pages: (comboPagesOptions || []).map(
              (e) => e?.dynamic_link_id
            ),
            [discount_form_keys.dynamic_link_uuids]: (
              selectedRecordData?.dynamic_links || []
            ).map((e) => e?.dynamic_link_id),
          }
        : {
            type: 1,
            code: "",
            currency: getUserCurrency(),
            applicability: COUPON_APPLICABLE_TYPE.EVERYONE,
            customerEligibilityType: CustomerEligibility.everyone,
            listing_uuids: listingMultiSelect ? [] : "",
            audience: {
              extras: [],
              groups: [],
              segment: [],
              booked_listing_uids: [],
            },
            [discount_form_keys.instalment_distribution_mechanism]:
              instalment_discount_types.BOTTOM_TO_TOP,
            [discount_form_keys.discount_application_scope]: Object.values(
              DISCOUNT_PAYMENT_PLAN_TYPE
            ),
            listInputFocused: false,
            combo_pages: [],
            [discount_form_keys.dynamic_link_uuids]: [],
            loading: false,
          };
    }, [isEdit, selectedRecordData, listingMultiSelect]);

    return (
      <ThemeProvider theme={creatorToolTheme}>
        <Form
          initialValues={initialValues}
          onSubmit={async (values, showOfferingVariationsDropdown) => {
            let response;
            if (isEdit) {
              response = await editDiscountCoupon({
                formState: values,
                discount_uid: selectedRecordData?.uuid,
              });
            } else {
              response = await addDiscountCoupon(
                values,
                showOfferingVariationsDropdown
              );
            }
            onSubmit(response);
          }}
          validate={(formValues) => {
            let errors = {};

            if (
              !checkIsArrayEmpty(formValues.audience?.booked_listing_uids) &&
              (!checkIsArrayEmpty(formValues.audience?.segment) ||
                !checkIsArrayEmpty(formValues.audience?.extras) ||
                !checkIsArrayEmpty(formValues.audience?.groups))
            ) {
              errors.audience =
                "Can't club any other group with offering customer!";
            }
            return errors;
          }}
        >
          {({ handleSubmit, values, form, ...restFormProps }) => {
            formRef.current = form;
            const extrasCount = values.audience?.extras?.length;
            const [offeringVariationsoptions, setOfferingVariationsoptions] =
              React.useState([]);
            const [
              selectedOfferingWithVariation,
              setSelectedOfferingWithVariation,
            ] = React.useState({});

            React.useEffect(() => {
              if (extrasCount) {
                form.change(discount_form_keys.is_promotional_coupon, false);
              }
            }, [extrasCount, form]);

            const isSpecificCombosSelected =
              values.applicability === COUPON_APPLICABLE_TYPE.SPECIFIC_COMBOS;

            const handleChangeApplicability = (value) => {
              if (value == COUPON_APPLICABLE_TYPE.LOYALTY_USERS) {
                form.change(discount_form_keys.audience, {
                  ...values.audience,
                  groups: ["all"],
                  extras: [],
                });
              } else {
                form.change(discount_form_keys.audience, {
                  ...values.audience,
                  extras: [],
                });
              }

              // Clear discount application scope when specific combos is selected
              if (value == COUPON_APPLICABLE_TYPE.SPECIFIC_COMBOS) {
                form.change(discount_form_keys.discount_application_scope, []);
              }
            };

            const showOfferingVariationsDropdown = React.useMemo(() => {
              const { listing_uuids, discount_application_scope } =
                values ?? {};
              if (
                is_empty(listing_uuids) ||
                is_empty(discount_application_scope) ||
                listing_uuids.length > 1
              ) {
                return false;
              }
              const isVariantTypeSelected = discount_application_scope.includes(
                DISCOUNT_PAYMENT_PLAN_TYPE.VARIATIONS
              );
              if (!isVariantTypeSelected) {
                return false;
              }
              const selectedOfferingData = offeringsOptions?.find(
                (item) =>
                  listing_uuids.includes(item.uuid) &&
                  item.is_offering_variants_enabled
              );
              const offeringSelectedHasVariations =
                isVariantTypeSelected && !is_empty(selectedOfferingData);
              if (offeringSelectedHasVariations) {
                setSelectedOfferingWithVariation(selectedOfferingData);
              }
              return offeringSelectedHasVariations;
            }, [
              values?.listing_uuids,
              values?.discount_application_scope,
              offeringsOptions,
            ]);

            const listingUuid = Array.isArray(values?.listing_uuids)
              ? values.listing_uuids.length === 1
                ? values.listing_uuids[0]
                : null
              : values?.listing_uuids || null;

            const showPaymentLinkDropdown = React.useMemo(() => {
              const {
                listing_uuids,
                discount_application_scope,
                applicability,
              } = values ?? {};
              if (applicability !== COUPON_APPLICABLE_TYPE.SPECIFIC_LISTINGS)
                return false;
              if (is_empty(listing_uuids)) return false;
              if (is_empty(discount_application_scope)) return false;
              if (
                Array.isArray(listing_uuids)
                  ? listing_uuids.length !== 1
                  : false
              )
                return false;
              return discount_application_scope.includes(
                DISCOUNT_PAYMENT_PLAN_TYPE.CUSTOM_PAYMENT_LINKS
              );
            }, [
              values?.listing_uuids,
              values?.discount_application_scope,
              values?.applicability,
            ]);

            const currencies = React.useMemo(
              () =>
                values?.currency && values?.type == DISCOUNT_TYPE.AMOUNT
                  ? [values?.currency]
                  : LTO_SUPPORTED_CURRENCIES,
              [values?.currency, values?.type]
            );

            const {
              data: paymentLinksOptions,
              loading: paymentLinksLoader,
              lastElementRef: lastElementPaymentLinksRef,
            } = useFetchCustomCodeComboPage({
              paymentLinkType: [LINK_TYPES.CUSTOM_LINK],
              listingUuid,
              initialData: { currencies },
            });

            const paymentLinkOptions = React.useMemo(() => {
              const options = paymentLinksOptions || [];
              if (!isEdit || !selectedRecordData?.dynamic_links?.length)
                return options;

              const editOptions = selectedRecordData.dynamic_links.map(
                (item) => ({
                  ...item,
                  description: item.listing_title,
                  label: item?.title,
                  value: item.dynamic_link_id,
                })
              );

              const merged = [...editOptions, ...options];
              return Array.from(
                new Map(merged.map((item) => [item.value, item])).values()
              );
            }, [paymentLinksOptions, isEdit, selectedRecordData]);

            // On Currency Change Reset Dynamic Links
            React.useEffect(() => {
              form.change(discount_form_keys.dynamic_link_uuids, []);
            }, [currencies]);

            React.useEffect(() => {
              if (
                !showOfferingVariationsDropdown &&
                !is_empty(offeringVariationsoptions)
              ) {
                return setOfferingVariationsoptions([]);
              }
              const setOfferingVariation = async () => {
                try {
                  const response = await fetchOfferingVariations({
                    offeringUuid: selectedOfferingWithVariation.uuid,
                  });
                  if (isRequestSuccessful(response.status)) {
                    const options = (
                      response.data?.page_data ||
                      response.data?.offering_variants
                    ).map((item) => ({
                      ...selectedOfferingWithVariation,
                      ...item,
                      label: item.title,
                      value: item.uuid,
                      price_per_head: item.domestic_price,
                      price_international: item.international_price,
                    }));
                    setOfferingVariationsoptions(options);
                  }
                } catch (error) {
                  logError({ error });
                }
              };

              setOfferingVariation();
            }, [showOfferingVariationsDropdown, selectedOfferingWithVariation]);

            return (
              <div className={styles.createDiscount}>
                <WrapperComponent {...{ ...restFormProps }}>
                  <form
                    className={classes.form}
                    onSubmit={(value, showOfferingVariationsDropdown) =>
                      handleSubmit(value, showOfferingVariationsDropdown)
                    }
                  >
                    <Field
                      name="type"
                      component={ExlyDropdown}
                      size="small"
                      fullWidth
                      dropdownLabel={{
                        label: "Type",
                        size: "small",
                        weight: "semi-bold",
                      }}
                      options={DISCOUNT_TYPE_OPTIONS}
                      onChange={() => {
                        form.change("currency_type", getUserCurrency());
                      }}
                      disabled={isEdit}
                    />

                    <Field
                      name="code"
                      component={ExlyInput}
                      labelClassName={classes.inputLabel}
                      fullWidth
                      label="Discount Code"
                      placeholder="Enter discount code e.g EXLY50"
                      helperText="Customers will enter this discount code at checkout."
                      size="small"
                      format={(value) => value?.toUpperCase()}
                      parse={(value) => value?.toUpperCase()}
                      validate={validateDiscountCode}
                      disabled={isEdit}
                    />

                    {!isInternationalTimezone() && values.type == 2 && (
                      <Field
                        component={ExlyDropdown}
                        name="currency"
                        fullWidth
                        disabled={isEdit}
                        size="small"
                        dropdownLabel={{
                          label: "Currency",
                          size: "small",
                          weight: "semi-bold",
                        }}
                        options={DISCOUNT_CURRENCY_OPTIONS}
                      />
                    )}

                    <Field
                      name="value"
                      component={ExlyInput}
                      labelClassName={classes.inputLabel}
                      fullWidth
                      disabled={isEdit}
                      type="number"
                      size="small"
                      label="Discount Value"
                      placeholder="Enter discount value"
                      validate={(value) =>
                        validateDiscountValue({
                          discountValue: value,
                          type: values.type,
                        })
                      }
                    />

                    <Field
                      name={discount_form_keys.usage_limit}
                      component={ExlyInput}
                      labelClassName={classes.inputLabel}
                      fullWidth
                      type="number"
                      label="Usage Limit"
                      placeholder="Usage Limit eg. 10"
                      size="small"
                      validate={(value) =>
                        validateUsageLimit({
                          usageLimit: value,
                        })
                      }
                    />
                    {isEdit && (
                      <div>
                        <div
                          className={`tw-font-medium tw-text-[#272522] tw-text-[14px] tw-mb-1`}
                        >
                          Expiry Date
                        </div>
                        <Field
                          name={discount_form_keys.expiry}
                          component={ExlyDatePicker}
                          validate={(value) =>
                            is_empty(value) ? "Required" : null
                          }
                          labelClassName={classes.inputLabel}
                          fullWidth
                          type="number"
                          label="Expiry"
                          placeholder="Expire date eg. 10/09/2025"
                          size="small"
                          minDate={new Date()}
                          format={(value) => (value ? new Date(value) : null)}
                          parse={(value) =>
                            format(
                              new Date(value),
                              DEFAULT_YEAR_MONTH_DATE_FORMAT
                            )
                          }
                        />
                      </div>
                    )}

                    <Field
                      disabled={isEdit}
                      name="applicability"
                      component={ExlyDropdown}
                      size="small"
                      fullWidth
                      dropdownLabel={{
                        label: "Applicability of this discount code",
                        size: "small",
                        weight: "semi-bold",
                      }}
                      options={COUPON_APPLICABLE_OPTIONS}
                      onChange={handleChangeApplicability}
                    />

                    {!isSpecificCombosSelected && (
                      <div>
                        <div
                          className={`tw-font-medium tw-text-[#272522] tw-text-[14px] tw-mb-1`}
                        >
                          Discount can be applied on
                        </div>

                        <Field
                          name={discount_form_keys.discount_application_scope}
                          multiple
                          placeholder={"Select"}
                          disabled={isEdit}
                          component={ExlyCustomSelect}
                          validate={(value) =>
                            is_empty(value) ? "Required" : null
                          }
                          input={{
                            value:
                              values[
                                discount_form_keys.discount_application_scope
                              ],
                            multiple: true,
                            disabled: isEdit,
                            onChange: (value) => {
                              form.change(
                                discount_form_keys.discount_application_scope,
                                value
                              );
                            },
                          }}
                          options={discount_application_scope_options}
                          renderOption={({ option, isSelected, onChange }) => (
                            <div
                              onClick={() => onChange(option.value)}
                              className="tw-border-0 tw-border-b-[1.5px] tw-border-b-[#E7E6E4] tw-border-solid tw-py-[12px] tw-px-[16px] tw-flex tw-items-center tw-gap-2"
                            >
                              <Checkbox
                                checked={isSelected}
                                tabIndex={-1}
                                size="large"
                              />
                              <div className="tw-text-[14px]">
                                {option.label}
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    )}

                    {values[
                      discount_form_keys.discount_application_scope
                    ]?.includes(PAYMENT_PLAN_TYPE.INSTALLMENTS) && (
                      <Field
                        disabled={isEdit}
                        name={
                          discount_form_keys.instalment_distribution_mechanism
                        }
                        component={ExlyDropdown}
                        placeholder={"Select"}
                        size="small"
                        fullWidth
                        dropdownLabel={{
                          label:
                            "Select how the discount will be applied across instalments",
                          size: "small",
                          weight: "semi-bold",
                        }}
                        options={instalment_discount_types_options}
                      />
                    )}

                    {/* Combo selector field - shown when specific combos is selected */}
                    {values.applicability ===
                      COUPON_APPLICABLE_TYPE.SPECIFIC_COMBOS && (
                      <Field
                        name={discount_form_keys.combo_pages}
                        component={CustomCodeSelect}
                        fullWidth
                        disabled={isEdit}
                        size="small"
                        options={comboPagesOptions}
                        loading={comboPagesLoader}
                        showStatus={true}
                        label="Select Combo Package(s)"
                        mobileModalTitle="Select Combo Package(s)"
                        placeholder="Select Combo Packages"
                        multiple
                        showChips
                        renderOption={(renderProps) => (
                          <ComboPageOption
                            showCheckbox
                            showStatus
                            lastElementRef={lastElementComboPagesRef}
                            {...renderProps}
                          />
                        )}
                        headerComponent={
                          checkIsArrayEmpty(comboPagesOptions) &&
                          !comboPagesLoader && <EmptySalesPageView />
                        }
                        validate={(value) =>
                          !value || value.length === 0
                            ? "Please select at least one combo package"
                            : undefined
                        }
                      />
                    )}

                    {!isEdit &&
                      values.applicability ==
                        COUPON_APPLICABLE_TYPE.LOYALTY_USERS && (
                        <div>
                          <label className={classes.customerEligibilityHeading}>
                            Customer eligibility
                          </label>
                          <div className={classes.customerEligibilityOptions}>
                            <Field
                              disabled={isEdit}
                              name="customerEligibility"
                              component={ExlyRadioGroup}
                              isRow
                              selected={customerEligibilityRadioOptions.find(
                                (option) =>
                                  option.value ===
                                  values.customerEligibilityType
                              )}
                              options={customerEligibilityRadioOptions}
                              onChange={(value) => {
                                onCustomerEligibilityChange({
                                  value,
                                  form,
                                  formValues: values,
                                });
                              }}
                            />
                          </div>
                        </div>
                      )}

                    {(values.applicability ==
                      COUPON_APPLICABLE_TYPE.SPECIFIC_LISTINGS ||
                      values.applicability ==
                        COUPON_APPLICABLE_TYPE.LOYALTY_USERS) && (
                      <Field
                        disabled={isEdit}
                        name="listing_uuids"
                        component={ExlyOfferingSelect}
                        label={
                          <div className={`tw-flex tw-flex-row tw-gap-2`}>
                            <div>Select specific offerings</div>
                            <Tooltip
                              color="primary"
                              arrow
                              title="Select the offerings on which this coupon can be applied"
                            >
                              <Info className={classes.infoIcon} />
                            </Tooltip>
                          </div>
                        }
                        multiple={listingMultiSelect}
                        mobileModalTitle="Select Offerings"
                        placeholder="Select Offerings"
                        selectAllContent="Select All"
                        deselectAllContent="Remove All"
                        options={offeringsOptions}
                        lastElementRef={offeringsLastElementRef}
                        loading={offeringsLoading}
                        headerComponent={
                          <OfferingSearchHeader
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            debouncedSearchQuery={debouncedSearchQuery}
                            offeringsLoading={offeringsLoading}
                            infiniteOptions={infiniteOptions}
                          />
                        }
                        fullWidth
                        showStatus
                        validate={validateSelectOfferings}
                      />
                    )}

                    {showOfferingVariationsDropdown &&
                      !is_empty(offeringVariationsoptions) && (
                        <Field
                          disabled={isEdit}
                          name="offering_variant_uuids"
                          component={ExlyOfferingSelect}
                          label={
                            <div className={`tw-flex tw-flex-row tw-gap-2`}>
                              <div>Select offering variation</div>
                              <Tooltip
                                color="primary"
                                arrow
                                title="Select the variations on which this coupon can be applied"
                              >
                                <Info className={classes.infoIcon} />
                              </Tooltip>
                            </div>
                          }
                          multiple={listingMultiSelect}
                          mobileModalTitle="Select variations"
                          placeholder="Select variations"
                          selectAllContent="Select All"
                          deselectAllContent="Remove All"
                          options={offeringVariationsoptions}
                          fullWidth
                          showStatus
                          validate={validateSelectVariations}
                        />
                      )}

                    {showPaymentLinkDropdown &&
                      !is_empty(paymentLinkOptions) && (
                        <Field
                          name={discount_form_keys.dynamic_link_uuids}
                          component={CustomCodeSelect}
                          fullWidth
                          disabled={isEdit}
                          size="small"
                          options={paymentLinkOptions}
                          loading={paymentLinksLoader}
                          showStatus={true}
                          label={`Select specific Payment Link(s) - Optional`}
                          mobileModalTitle="Select specific Payment Link(s) - Optional"
                          placeholder="Select specific Payment Links"
                          multiple
                          showChips
                          renderOption={(renderProps) => (
                            <ComboPageOption
                              showCheckbox
                              showStatus
                              lastElementRef={lastElementPaymentLinksRef}
                              {...renderProps}
                            />
                          )}
                          headerComponent={
                            checkIsArrayEmpty(paymentLinkOptions) &&
                            !paymentLinksLoader && <EmptySalesPageView />
                          }
                        />
                      )}

                    {values.applicability ==
                      COUPON_APPLICABLE_TYPE.LOYALTY_USERS &&
                      values.customerEligibilityType ==
                        CustomerEligibility.specificGroups && (
                        <Field
                          disabled={isEdit}
                          name="audience"
                          label={
                            <div className={`tw-flex tw-flex-row tw-gap-2`}>
                              <div>Select specific group or customers</div>
                              <Tooltip
                                color="primary"
                                arrow
                                title="Select the audience who can apply this coupon"
                              >
                                <Info className={classes.infoIcon} />
                              </Tooltip>
                            </div>
                          }
                          component={ExlyAutoComplete}
                          filterOptions={(options) => options}
                          multiple
                          format={(value) => {
                            return (
                              value && [
                                ...value.groups.map((group) => ({
                                  key: group,
                                  groupBy:
                                    "Recipient Groups (Click on group to add)",
                                })),
                                ...value.extras.map((customer) => ({
                                  key: customer,
                                  groupBy: "Recipients",
                                })),
                                ...value.booked_listing_uids.map(
                                  (listingData) => ({
                                    ...listingData,
                                    key: listingData.key,
                                    groupBy: "Offering customers",
                                  })
                                ),
                                ...value.segment.map((segmentData) => ({
                                  ...segmentData,
                                  key: segmentData.key,
                                  groupBy: "Segments",
                                })),
                              ]
                            );
                          }}
                          onChange={(value) => {
                            onChangeAudience({
                              value,
                              form,
                              formValues: values,
                            });
                          }}
                          disableCloseOnSelect
                          onClose={resetAudienceFilter}
                          options={audienceOptions}
                          groupBy={(option) => option.groupBy}
                          getOptionLabel={(option) => option.key}
                          inputValue={audienceSearchQuery}
                          inputProps={{
                            placeholder: "Add specific group or customers",
                            onChange: ({ target: { value } }) => {
                              setAudienceSearchQuery(value);
                            },
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                label={option.key}
                                {...getTagProps({ index })}
                                onDelete={() =>
                                  handleDeleteOption(option, form)
                                }
                              />
                            ))
                          }
                          renderOption={(option) => {
                            const checked =
                              values.audience.groups.includes(option.key) ||
                              values.audience.groups.includes(
                                formatAudienceKey(option.key)
                              ) ||
                              values.audience.extras.includes(option.key) ||
                              values.audience.booked_listing_uids.find(
                                (item) => item.value === option.value
                              ) ||
                              values.audience.segment.find(
                                (item) => item.value === option.value
                              );

                            const lastAudienceOption =
                              audienceOptions[audienceOptions.length - 1];

                            const isLast =
                              lastAudienceOption?.key === option.key &&
                              lastAudienceOption?.groupBy === option.groupBy;

                            return (
                              <div
                                className={classes.autoCompleteItem}
                                ref={
                                  isLast ? audienceLastElementRef : undefined
                                }
                              >
                                {option.isSegment ? (
                                  <Radio
                                    disabled={isEdit}
                                    checked={!!checked}
                                  />
                                ) : (
                                  <Checkbox
                                    disabled={isEdit}
                                    size="large"
                                    checked={!!checked}
                                  />
                                )}
                                <Avatar label={option.key} />
                                <span className={classes.customerTitle}>
                                  {option.key}
                                </span>
                              </div>
                            );
                          }}
                        />
                      )}

                    <Field
                      name="is_promotional_coupon"
                      component={ExlyCheckbox}
                      id="confirmPromoteCode"
                      size="large"
                      label="Promote Coupon Code"
                      helperNode={
                        <Tooltip
                          color="primary"
                          arrow
                          title={`The coupon will be promoted to your users on the ${
                            isSpecificCombosSelected ? "combo page" : "listing"
                          }`}
                        >
                          <Info className={classes.infoIcon} />
                        </Tooltip>
                      }
                      disabled={values.audience?.extras?.length || isEdit}
                    />

                    {!isInternationalTimezone() && (
                      <Field
                        disabled={isEdit}
                        name="include_gst"
                        component={ExlyCheckbox}
                        id="confirmGST"
                        size="large"
                        label="Include the price in GST"
                        checked={values.include_gst}
                        onChange={(checked) =>
                          form.change(discount_form_keys.include_gst, checked)
                        }
                      />
                    )}

                    {!isEdit && (
                      <div className={`${classes.altInfoBanner}`}>
                        <Info /> Discount codes automatically expire in 30 days.
                      </div>
                    )}
                  </form>
                </WrapperComponent>
              </div>
            );
          }}
        </Form>
      </ThemeProvider>
    );
  }
);

CreateDiscounts.displayName = "CreateDiscountCode";

export default CreateDiscounts;
